import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Typography,
  Box
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import PassengerManagement from '../common/PassengerManagement';
import { buildErrorMessage, toDateTimeMs, getPreviousSectorCutoffMs, formatPrevCutoffLabel, WAKE_TURBULENCE_OPTIONS, FLIGHT_RULES_OPTIONS, FACILITY_TYPE_OPTIONS, CHARTER_SERVICE_LABEL_OPTIONS, hoursBetweenMs, getCharterScheduleSpanHours, isLongerThanHours, formatDurationHours, LONG_CHARTER_HOURS } from './SalesEnquiryUtils';
import { getTimezoneLabel, displayDateTimeToUTC } from '../../utils/timezoneUtils';

const TripAndSectorDialogs = ({
  tripDialogOpen,
  setTripDialogOpen,
  sectorDialogOpen,
  setSectorDialogOpen,
  editingTrip,
  setEditingTrip,
  editingSector,
  setEditingSector,
  selectedTrip,
  sectors,
  setTrips,
  setSelectedTrip,
  setSectors,
  fetchTrips,
  fetchTripDetail,
  tripForm,
  setTripForm,
  sectorForm,
  setSectorForm,
  resetTripForm,
  resetSectorForm,
  handlePassengerUpdate,
  updateSector,
  displayTimezone = 'Asia/Kolkata'
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isSalesUser = user?.role === ROLES.SALES;

  const createTrip = async () => {
    try {
      const token = localStorage.getItem('token');
      const baggageWeight = tripForm.baggageWeight != null ? tripForm.baggageWeight : 20;
      const payload = {
        title: tripForm.title,
        flightType: tripForm.flightType || 'CHARTER',
        clientName: tripForm.clientName || undefined,
        clientContact: tripForm.clientContact?.email || tripForm.clientContact?.phone 
          ? tripForm.clientContact 
          : undefined,
        passengers: tripForm.passengers || [],
        totalWeight: tripForm.totalWeight || 0,
        baggageWeight,
        specialRequirements: tripForm.specialRequirements || undefined,
        itineraryDisplayStatus: tripForm.itineraryDisplayStatus || undefined,
        itineraryJourneyNotes: tripForm.itineraryJourneyNotes || undefined,
        remarks: tripForm.newRemark || undefined,
        salesTemplateId: tripForm.salesTemplateId || 'default',
        requiresBA: true,
        requiresATCClearance: true
      };
      const response = await axios.post('/api/sales/trips', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const createdTrip = response.data;
      setTrips(prev => [createdTrip, ...prev]);
      setTripDialogOpen(false);
      resetTripForm();
      await fetchTrips();
      
      const charterNumber = createdTrip.charterNumber || 'N/A';
      const flightType = createdTrip.flightType || 'CHARTER';
      const successMessage = `Trip created successfully! Charter: ${charterNumber} | Title: "${createdTrip.title}" | Type: ${flightType} | Status: ${createdTrip.status || 'DRAFT'}. You can now add sectors to this trip.`;
      showToast(successMessage, 'success');
    } catch (error) {
      console.error('Failed to create trip:', error);
      let errorMessage = 'Failed to create trip';
      const errorDetails = error.response?.data;
      
      if (errorDetails?.errors && Array.isArray(errorDetails.errors) && errorDetails.errors.length > 0) {
        const validationErrors = errorDetails.errors.map(err => {
          const field = err.param || err.path || 'field';
          const msg = err.msg || err.message || 'Invalid value';
          return `${field}: ${msg}`;
        }).join('; ');
        errorMessage = `Trip creation failed - Validation errors: ${validationErrors}`;
      } else if (errorDetails?.message) {
        errorMessage = `Trip creation failed: ${errorDetails.message}`;
      } else if (error.response?.status === 403) {
        errorMessage = 'Trip creation failed: You do not have permission to create trips.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Trip creation failed: Your session has expired. Please log in again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Trip creation failed: Server error. Please try again later or contact support.';
      } else {
        errorMessage = `Trip creation failed: ${error.message || 'An unexpected error occurred'}`;
      }
      
      showToast(errorMessage, 'error');
    }
  };

  const updateTrip = async () => {
    try {
      const token = localStorage.getItem('token');
      const { newRemark, ...tripData } = tripForm;
      const payload = {
        ...tripData,
        passengers: tripForm.passengers || [],
        totalWeight: tripForm.totalWeight || 0,
        baggageWeight: tripForm.baggageWeight != null ? tripForm.baggageWeight : 20,
        newRemark: newRemark || undefined
      };
      const response = await axios.put(`/api/sales/trips/${editingTrip._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(prev => prev.map(t => t._id === editingTrip._id ? response.data : t));
      if (selectedTrip?._id === editingTrip._id) {
        setSelectedTrip(response.data);
        await fetchTripDetail(editingTrip._id);
      }
      setTripDialogOpen(false);
      setEditingTrip(null);
      resetTripForm();
      
      const charterNumber = response.data.charterNumber || 'N/A';
      showToast(`Trip updated successfully! Charter: ${charterNumber} | Title: "${response.data.title}" | Status: ${response.data.status}`, 'success');
    } catch (error) {
      console.error('Failed to update trip:', error);
      showToast(buildErrorMessage('Trip update', error), 'error');
    }
  };

  // When adding a sector, validate that departure/arrival are not before the previous sector's arrival.
  // When editing, validate against the sector before the one being edited (not the last sector/itself).
  const referenceSector = (() => {
    if (editingSector) {
      const idx = sectors.findIndex(s => s._id === editingSector._id);
      return idx > 0 ? sectors[idx - 1] : null;
    }
    return sectors.length >= 1 ? sectors[sectors.length - 1] : null;
  })();
  const prevCutoffMs = referenceSector ? getPreviousSectorCutoffMs(referenceSector) : null;
  const prevCutoffLabel = referenceSector ? formatPrevCutoffLabel(referenceSector, displayTimezone) : '';

  const departureMs = toDateTimeMs(sectorForm.departure?.date, sectorForm.departure?.time);
  const arrivalMs = toDateTimeMs(sectorForm.arrival?.estimatedDate, sectorForm.arrival?.estimatedTime);

  const departureTooEarly = prevCutoffMs != null && departureMs != null && departureMs < prevCutoffMs;
  const arrivalTooEarly = prevCutoffMs != null && arrivalMs != null && arrivalMs < prevCutoffMs;
  const hasDateTimeValidationErrors = departureTooEarly || arrivalTooEarly;

  const sectorScheduleHours = hoursBetweenMs(departureMs, arrivalMs);
  const otherSectorsForSpan = editingSector
    ? (sectors || []).filter((s) => String(s._id) !== String(editingSector._id))
    : (sectors || []);
  const charterScheduleHours = getCharterScheduleSpanHours(otherSectorsForSpan, [
    { start: departureMs, end: arrivalMs }
  ]);
  const showLongScheduleWarning =
    isLongerThanHours(sectorScheduleHours) || isLongerThanHours(charterScheduleHours);

  const createSector = async () => {
    try {
      if (hasDateTimeValidationErrors) {
        showToast('Departure and estimated arrival must be on or after the previous sector\'s estimated arrival (or departure if not set).', 'warning');
        return;
      }
      console.log('Sector form before submission:', JSON.stringify(sectorForm, null, 2));
      if (!selectedTrip || !selectedTrip._id) {
        showToast('Please select a charter first before adding a sector.', 'warning');
        return;
      }
      
      const token = localStorage.getItem('token');
      
      const departureTime = (sectorForm.departure.time && sectorForm.departure.time.trim()) || '';
      if (!departureTime) {
        showToast('Please select a departure time before creating the sector.', 'warning');
        return;
      }

      const departureDateISO = (sectorForm.departure.date && sectorForm.departure.date.trim())
        ? (displayDateTimeToUTC(
            sectorForm.departure.date.trim(),
            departureTime,
            displayTimezone
          ) ?? '')
        : '';
      
      const arrivalDateISO = (sectorForm.arrival.estimatedDate && sectorForm.arrival.estimatedDate.trim())
        ? (displayDateTimeToUTC(
            sectorForm.arrival.estimatedDate.trim(),
            (sectorForm.arrival.estimatedTime && sectorForm.arrival.estimatedTime.trim()) || '00:00',
            displayTimezone
          ) ?? undefined)
        : undefined;
      
      const payload = {
        seqNo: sectorForm.seqNo || 1,
        departure: {
          airport: sectorForm.departure.airport || '',
          date: departureDateISO,
          time: departureTime,
          fullName: sectorForm.departure.fullName || undefined,
          coordinates: sectorForm.departure.coordinates || undefined,
          facilityType: sectorForm.departure.facilityType || undefined
        },
        arrival: {
          airport: sectorForm.arrival.airport || '',
          estimatedDate: arrivalDateISO,
          estimatedTime: sectorForm.arrival.estimatedTime || undefined,
          fullName: sectorForm.arrival.fullName || undefined,
          coordinates: sectorForm.arrival.coordinates || undefined,
          facilityType: sectorForm.arrival.facilityType || undefined
        },
        wakeTurbulence: sectorForm.wakeTurbulence || undefined,
        flightRules: sectorForm.flightRules || undefined,
        flightType: sectorForm.flightType || 'CHARTER',
        charterServiceLabel: sectorForm.charterServiceLabel || undefined,
        itineraryNotes: sectorForm.itineraryNotes || undefined,
        groundTransferNotes: sectorForm.groundTransferNotes || undefined,
        departureGroundHandler: sectorForm.departureGroundHandler || undefined,
        arrivalGroundHandler: sectorForm.arrivalGroundHandler || undefined,
        departureTransport: sectorForm.departureTransport || undefined,
        arrivalTransport: sectorForm.arrivalTransport || undefined,
        remarks: sectorForm.remarks || undefined
      };
      
      console.log('Sending sector payload:', JSON.stringify(payload, null, 2));
      
      const response = await axios.post(`/api/sales/trips/${selectedTrip._id}/sectors`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const createdSector = response.data;
      setSectors(prev => [...prev, createdSector]);
      setSectorDialogOpen(false);
      resetSectorForm();
      await fetchTripDetail(selectedTrip._id);
      
      const charterNumber = selectedTrip.charterNumber || 'N/A';
      showToast(`Sector ${createdSector.seqNo} created successfully! Charter: ${charterNumber} | Route: ${createdSector.departure?.airport || 'N/A'} → ${createdSector.arrival?.airport || 'N/A'} | Status: ${createdSector.status || 'PLANNED'}`, 'success');
    } catch (error) {
      console.error('Failed to create sector:', error);
      console.error('Error response:', error.response?.data);
      showToast(buildErrorMessage('Sector creation', error), 'error');
    }
  };

  return (
    <>
      {/* Trip Dialog */}
      <Dialog open={tripDialogOpen} onClose={() => setTripDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTrip ? 'Edit Trip' : 'Create New Trip'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Trip Title"
            value={tripForm.title}
            onChange={(e) => setTripForm(prev => ({ ...prev, title: e.target.value }))}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Flight Type</InputLabel>
            <Select
              value={tripForm.flightType}
              onChange={(e) => setTripForm(prev => ({ ...prev, flightType: e.target.value }))}
            >
              <MenuItem value="CHARTER">Charter</MenuItem>
              <MenuItem value="NON_REVENUE">Non-Revenue</MenuItem>
              <MenuItem value="TRAINING">Training</MenuItem>
              <MenuItem value="FERRY">Ferry</MenuItem>
              <MenuItem value="POSITIONING">Positioning</MenuItem>
              <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Client Name"
            value={tripForm.clientName}
            onChange={(e) => setTripForm(prev => ({ ...prev, clientName: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Client Email"
            type="email"
            value={tripForm.clientContact?.email || ''}
            onChange={(e) => setTripForm(prev => ({
              ...prev,
              clientContact: { ...(prev.clientContact || {}), email: e.target.value }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Client Phone"
            value={tripForm.clientContact?.phone || ''}
            onChange={(e) => setTripForm(prev => ({
              ...prev,
              clientContact: { ...(prev.clientContact || {}), phone: e.target.value }
            }))}
            margin="normal"
          />
          <Divider sx={{ my: 2 }} />
          <PassengerManagement
            tripId={editingTrip?._id}
            passengers={tripForm.passengers}
            onUpdate={handlePassengerUpdate}
            optionalBaggageWeight={tripForm.baggageWeight != null ? tripForm.baggageWeight : 20}
            readOnly={(() => {
              if (!editingTrip) return false;
              const canOverride = [ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role);
              const isCompletedCharter = ['COMPLETED', 'CHARTER_COMPLETED'].includes(editingTrip.status);
              const hasClearedSector = sectors.some(s => ['CLEARED', 'ATC_PASSED', 'COMPLETED', 'SECTOR_COMPLETED'].includes(s.status));
              const canEdit = !hasClearedSector || canOverride || isCompletedCharter;
              return !canEdit;
            })()}
          />
          <TextField
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 0.1 }}
            label="Baggage Weight (kg) - Optional"
            value={tripForm.baggageWeight ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? null : parseFloat(e.target.value);
              const baggage = (v != null && !isNaN(v)) ? v : 20;
              setTripForm(prev => {
                const passengerSum = (prev.passengers || []).reduce((s, p) => s + (Number(p.weight) || 0), 0);
                return {
                  ...prev,
                  baggageWeight: v,
                  totalWeight: passengerSum + baggage
                };
              });
            }}
            margin="normal"
            helperText="Default 20 kg if left empty. Included in trip total weight."
          />
          <TextField
            fullWidth
            label="Special Requirements"
            value={tripForm.specialRequirements}
            onChange={(e) => setTripForm(prev => ({ ...prev, specialRequirements: e.target.value }))}
            margin="normal"
            multiline
            rows={2}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Passenger Itinerary
          </Typography>
          <TextField
            fullWidth
            label="Itinerary Display Status"
            value={tripForm.itineraryDisplayStatus || ''}
            onChange={(e) => setTripForm(prev => ({ ...prev, itineraryDisplayStatus: e.target.value }))}
            margin="normal"
            placeholder="e.g. CONFIRMED"
            helperText="Shown on passenger PDF. Leave blank to auto-map from ops status (CONFIRMED after crew acceptance)."
          />
          <TextField
            fullWidth
            label="Journey Notes (Itinerary)"
            value={tripForm.itineraryJourneyNotes || ''}
            onChange={(e) => setTripForm(prev => ({ ...prev, itineraryJourneyNotes: e.target.value }))}
            margin="normal"
            multiline
            rows={2}
            helperText="Optional overview note on the passenger journey itinerary"
          />
          <TextField
            fullWidth
            label="Add Remark (Optional)"
            value={tripForm.newRemark}
            onChange={(e) => setTripForm(prev => ({ ...prev, newRemark: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
            helperText="This remark will be added to the remarks history"
          />
          {isSalesUser && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Template</InputLabel>
              <Select
                value={tripForm.salesTemplateId}
                onChange={(e) => setTripForm(prev => ({ ...prev, salesTemplateId: e.target.value }))}
              >
                <MenuItem value="default">Default Template</MenuItem>
                <MenuItem value="corporate">Corporate Template</MenuItem>
                <MenuItem value="charter">Charter Template</MenuItem>
              </Select>
            </FormControl>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 'medium' }}>
              Mandatory Compliance Requirements
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>BA Test</strong> and <strong>ATC Clearance</strong> are mandatory for all flights as per DGCA regulations. 
                These requirements must be completed for every flight.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTripDialogOpen(false)}>Cancel</Button>
          <Button onClick={editingTrip ? updateTrip : createTrip} variant="contained">
            {editingTrip ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sector Dialog */}
      <Dialog
        open={sectorDialogOpen}
        onClose={() => {
          setSectorDialogOpen(false);
          setEditingSector?.(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingSector ? 'Edit Sector' : 'Add Sector'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Dates and times are shown in the display timezone and stored in UTC.
          </Typography>
          {editingSector && (
            ['COMPLETED', 'CHARTER_COMPLETED'].includes(selectedTrip?.status)
            || ['COMPLETED', 'SECTOR_COMPLETED'].includes(editingSector?.status)
          ) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Correcting a completed sector/charter. Schedule and detail changes are saved without resetting sector status or crew assignment.
            </Alert>
          )}
          {!editingSector && ['COMPLETED', 'CHARTER_COMPLETED'].includes(selectedTrip?.status) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Adding a sector to a completed charter will reopen the charter workflow for the new sector (existing completed sectors stay as-is).
            </Alert>
          )}
          {showLongScheduleWarning && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {isLongerThanHours(sectorScheduleHours) && isLongerThanHours(charterScheduleHours)
                ? `This sector is ${formatDurationHours(sectorScheduleHours)} and the charter spans ${formatDurationHours(charterScheduleHours)} from first departure to last arrival (over ${LONG_CHARTER_HOURS} hours). Warning only — you can still save.`
                : isLongerThanHours(sectorScheduleHours)
                  ? `This sector is scheduled for ${formatDurationHours(sectorScheduleHours)} (over ${LONG_CHARTER_HOURS} hours). Warning only — you can still save.`
                  : `This charter spans ${formatDurationHours(charterScheduleHours)} from first departure to last arrival (over ${LONG_CHARTER_HOURS} hours). Warning only — you can still save.`}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Sequence Number"
            type="number"
            value={sectorForm.seqNo}
            onChange={(e) => setSectorForm(prev => ({ ...prev, seqNo: parseInt(e.target.value) }))}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="charter-service-label">Charter Service Label (Itinerary)</InputLabel>
            <Select
              labelId="charter-service-label"
              label="Charter Service Label (Itinerary)"
              value={sectorForm.charterServiceLabel || ''}
              onChange={(e) => setSectorForm(prev => ({ ...prev, charterServiceLabel: e.target.value }))}
            >
              <MenuItem value=""><em>Auto from aircraft type</em></MenuItem>
              {CHARTER_SERVICE_LABEL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>Departure</Typography>
          <TextField
            fullWidth
            label="Departure Airport / Code"
            value={sectorForm.departure.airport}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departure: { ...prev.departure, airport: e.target.value }
            }))}
            margin="normal"
            required
            helperText="Short code shown in route (e.g. BLR, COG)"
          />
          <TextField
            fullWidth
            label="Departure Facility Full Name"
            value={sectorForm.departure.fullName || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departure: { ...prev.departure, fullName: e.target.value }
            }))}
            margin="normal"
            placeholder="e.g. Bengaluru Heliport (Kempegowda Intl. Airport Area)"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="dep-facility-type">Departure Facility Type</InputLabel>
            <Select
              labelId="dep-facility-type"
              label="Departure Facility Type"
              value={sectorForm.departure.facilityType || ''}
              onChange={(e) => setSectorForm(prev => ({
                ...prev,
                departure: { ...prev.departure, facilityType: e.target.value }
              }))}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {FACILITY_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Departure Coordinates"
            value={sectorForm.departure.coordinates || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departure: { ...prev.departure, coordinates: e.target.value }
            }))}
            margin="normal"
            placeholder="Optional"
          />
          <TextField
            fullWidth
            label={`Departure Date (${getTimezoneLabel(displayTimezone)})`}
            type="date"
            value={sectorForm.departure.date}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departure: { ...prev.departure, date: e.target.value }
            }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
            error={departureTooEarly}
            helperText={departureTooEarly && prevCutoffLabel ? `Must be on or after ${prevCutoffLabel}` : ''}
          />
          <TextField
            fullWidth
            label={`Departure Time (${getTimezoneLabel(displayTimezone)})`}
            type="time"
            value={sectorForm.departure.time}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departure: { ...prev.departure, time: e.target.value }
            }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
            error={departureTooEarly}
            helperText={departureTooEarly && prevCutoffLabel ? `Must be on or after ${prevCutoffLabel}` : ''}
          />

          <Typography variant="subtitle2" sx={{ mt: 2 }}>Arrival</Typography>
          <TextField
            fullWidth
            label="Arrival Airport / Code"
            value={sectorForm.arrival.airport}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrival: { ...prev.arrival, airport: e.target.value }
            }))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Arrival Facility Full Name"
            value={sectorForm.arrival.fullName || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrival: { ...prev.arrival, fullName: e.target.value }
            }))}
            margin="normal"
            placeholder="e.g. Coorg Helipad (Madikeri, Karnataka)"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="arr-facility-type">Arrival Facility Type</InputLabel>
            <Select
              labelId="arr-facility-type"
              label="Arrival Facility Type"
              value={sectorForm.arrival.facilityType || ''}
              onChange={(e) => setSectorForm(prev => ({
                ...prev,
                arrival: { ...prev.arrival, facilityType: e.target.value }
              }))}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              {FACILITY_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Arrival Coordinates"
            value={sectorForm.arrival.coordinates || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrival: { ...prev.arrival, coordinates: e.target.value }
            }))}
            margin="normal"
            placeholder="Optional"
          />
          <TextField
            fullWidth
            label={`Estimated Arrival Date (${getTimezoneLabel(displayTimezone)})`}
            type="date"
            value={sectorForm.arrival.estimatedDate}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrival: { ...prev.arrival, estimatedDate: e.target.value }
            }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            error={arrivalTooEarly}
            helperText={arrivalTooEarly && prevCutoffLabel ? `Must be on or after ${prevCutoffLabel}` : ''}
          />
          <TextField
            fullWidth
            label={`Estimated Arrival Time (${getTimezoneLabel(displayTimezone)})`}
            type="time"
            value={sectorForm.arrival.estimatedTime}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrival: { ...prev.arrival, estimatedTime: e.target.value }
            }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            error={arrivalTooEarly}
            helperText={arrivalTooEarly && prevCutoffLabel ? `Must be on or after ${prevCutoffLabel}` : ''}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2">Itinerary notes & ground handling</Typography>
          <TextField
            fullWidth
            label="Passenger Notes (Itinerary)"
            value={sectorForm.itineraryNotes || ''}
            onChange={(e) => setSectorForm(prev => ({ ...prev, itineraryNotes: e.target.value }))}
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g. Arrive at the helipad 30 minutes prior to departure"
          />
          <TextField
            fullWidth
            label="Ground Transfer Notes"
            value={sectorForm.groundTransferNotes || ''}
            onChange={(e) => setSectorForm(prev => ({ ...prev, groundTransferNotes: e.target.value }))}
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g. Transfer from Coorg Helipad to Coorg Airstrip; baggage transferred to aircraft"
          />
          <TextField
            fullWidth
            label="Departure Transport"
            value={sectorForm.departureTransport?.vehicleDetails || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departureTransport: { vehicleDetails: e.target.value }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Arrival Transport"
            value={sectorForm.arrivalTransport?.vehicleDetails || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrivalTransport: { vehicleDetails: e.target.value }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Departure Ground Handler Contact"
            value={sectorForm.departureGroundHandler?.contactPerson || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departureGroundHandler: {
                ...(prev.departureGroundHandler || {}),
                contactPerson: e.target.value
              }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Departure Ground Handler Phone"
            value={sectorForm.departureGroundHandler?.contactNumber || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              departureGroundHandler: {
                ...(prev.departureGroundHandler || {}),
                contactNumber: e.target.value
              }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Arrival Ground Handler Contact"
            value={sectorForm.arrivalGroundHandler?.contactPerson || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrivalGroundHandler: {
                ...(prev.arrivalGroundHandler || {}),
                contactPerson: e.target.value
              }
            }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Arrival Ground Handler Phone"
            value={sectorForm.arrivalGroundHandler?.contactNumber || ''}
            onChange={(e) => setSectorForm(prev => ({
              ...prev,
              arrivalGroundHandler: {
                ...(prev.arrivalGroundHandler || {}),
                contactNumber: e.target.value
              }
            }))}
            margin="normal"
          />

          <Divider sx={{ my: 2 }} />
          <FormControl fullWidth margin="normal">
            <InputLabel id="wake-turbulence-label">Wake Turbulence</InputLabel>
            <Select
              labelId="wake-turbulence-label"
              label="Wake Turbulence"
              value={sectorForm.wakeTurbulence || ''}
              onChange={(e) => setSectorForm(prev => ({ ...prev, wakeTurbulence: e.target.value }))}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {WAKE_TURBULENCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="flight-rules-label">Flight Rules</InputLabel>
            <Select
              labelId="flight-rules-label"
              label="Flight Rules"
              value={sectorForm.flightRules || ''}
              onChange={(e) => setSectorForm(prev => ({ ...prev, flightRules: e.target.value }))}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {FLIGHT_RULES_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="sector-flight-type-label">Flight Type</InputLabel>
            <Select
              labelId="sector-flight-type-label"
              label="Flight Type"
              value={sectorForm.flightType || 'CHARTER'}
              onChange={(e) => setSectorForm(prev => ({ ...prev, flightType: e.target.value }))}
            >
              <MenuItem value="CHARTER">Charter</MenuItem>
              <MenuItem value="NON_REVENUE">Non-Revenue</MenuItem>
              <MenuItem value="TRAINING">Training</MenuItem>
              <MenuItem value="FERRY">Ferry</MenuItem>
              <MenuItem value="POSITIONING">Positioning</MenuItem>
              <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Add Remark (Optional)"
            value={sectorForm.remarks || ''}
            onChange={(e) => setSectorForm(prev => ({ ...prev, remarks: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
            helperText="This remark will be added to the sector remarks history"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSectorDialogOpen(false);
              setEditingSector?.(null);
            }}
          >
            Cancel
          </Button>
          {editingSector ? (
            <Button onClick={updateSector} variant="contained" disabled={hasDateTimeValidationErrors}>
              Update Sector
            </Button>
          ) : (
            <Button onClick={createSector} variant="contained" disabled={hasDateTimeValidationErrors}>
              Add Sector
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TripAndSectorDialogs;
